import { ILocation } from '@/Interfaces/Forecaster';
import GeoTargetingZipCodesUpload from '@/components/Geography/GeoTargetingZipCodesUpload';
import LocationChips from '@/components/Geography/LocationChips';
import LocationTypeLabel from '@/components/Geography/LocationTypeLabel';
import SearchIconAlt from '@/components/Icons/SearchIconAlt';
import useGeoLocationsPaste from '@/hooks/geo/useGeoLocationsPaste';
import { Autocomplete, AutocompleteInputChangeReason, InputAdornment, TextField } from '@mui/material';
import cn from 'classnames';
import { SyntheticEvent, memo, useCallback, useState } from 'react';
import { isLocationOptionEqualToValue } from '../helpers';

interface IProps {
  id: string;
  selectedLocations: ILocation[];
  loading: boolean;
  className?: string;
  suggestions: ILocation[];
  handleSearch: (searchTerm: string) => void;
  handleSelectionChange: (value: ILocation[]) => void;
  handleClearSelection: () => void;
  handleLocationRemove: (id: ILocation['id']) => () => void;
  handleNotFoundLocations: (notFoundLocations: ILocation[]) => void;
  handleCSVUpload?: (zipCodes: string[]) => Promise<void>;
}

const SearchLocations = memo(
  ({
    id,
    className,
    selectedLocations = [],
    loading,
    suggestions,
    handleSearch,
    handleSelectionChange,
    handleClearSelection,
    handleLocationRemove,
    handleNotFoundLocations,
    handleCSVUpload,
  }: IProps) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

    const { onValuesPaste, loading: processingPastedValues } = useGeoLocationsPaste(
      selectedLocations,
      handleSelectionChange,
      handleNotFoundLocations,
    );

    const _handleSearch = useCallback(
      (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason === 'input') {
          setIsDropdownOpen(true);
          setSearchTerm(value);
          handleSearch(value);
        }
      },
      [handleSearch],
    );

    const _handleSelectionChange = useCallback(
      (_: SyntheticEvent, value: ILocation[]) => {
        handleSelectionChange(value);
      },
      [handleSelectionChange],
    );

    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <Autocomplete
          id={id}
          multiple
          fullWidth
          disableClearable
          disableCloseOnSelect
          filterSelectedOptions
          size='small'
          value={selectedLocations}
          loading={loading || processingPastedValues}
          options={suggestions}
          onChange={_handleSelectionChange}
          getOptionLabel={option => option?.displayName || ''}
          isOptionEqualToValue={isLocationOptionEqualToValue}
          noOptionsText='Type to search'
          open={isDropdownOpen}
          inputValue={searchTerm}
          onInputChange={_handleSearch}
          onBlur={() => setIsDropdownOpen(false)}
          renderInput={props => (
            <TextField
              {...props}
              placeholder='Search by States, Markets, Zip Codes, Counties and Congressional Districts. Multiple zip codes can be pasted, separated by a comma or a space.'
              onPaste={onValuesPaste}
              onKeyDown={(event: any) => {
                // Prevent values from being removed when backspace or delete is pressed
                if (event.key === 'Backspace' || event.key === 'Delete') {
                  event.stopPropagation();
                }
              }}
              variant='outlined'
              slotProps={{
                input: {
                  ...props.InputProps,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIconAlt />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.id}>
                <span className='mr-auto'>{option.displayName}</span>
                <LocationTypeLabel className='ml-4' locationType={option.type} />
              </li>
            );
          }}
        />
        {/* @ts-expect-error js component props */}
        {!!handleCSVUpload && <GeoTargetingZipCodesUpload onUpload={handleCSVUpload} />}
        {!!selectedLocations?.length && (
          <LocationChips
            locations={selectedLocations}
            variant='success'
            onRemove={handleLocationRemove}
            onRemoveAll={handleClearSelection}
          />
        )}
      </div>
    );
  },
);

export default SearchLocations;
